import React, { FunctionComponent, useCallback, useState } from "react";
import {
  Button,
  InputAdornment,
  LinearProgress,
  TextField,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { Symbology } from "@scandit/web-datacapture-barcode";

import useStyles from "./styles";
import * as StoreInventoryApi from "../../../api/storeInventoryApi";
import { isOk } from "../../../result";
import { useI18n } from "../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../components/ScanditBarcodeScanner";
import { globalSettings } from "../../../models/Settings";

interface ConnectToPriceTagProps {
  addToStore?: boolean;
  productId?: string;
  storeId: string;
  gtin: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ConnectToPriceTag: FunctionComponent<ConnectToPriceTagProps> = ({
  gtin,
  storeId,
  productId,
  addToStore = false,
  onSuccess,
  onCancel
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [state, setState] = useState({
    priceTagBarCode: "",
    loading: false,
    error: ""
  });

  const addProductAndLinkPriceTag = useCallback(
    async (barCode: string) => {
      const trimmed = (barCode ?? "").trim();
      if (!trimmed) return;

      setState(s => ({ ...s, loading: true, error: "" }));

      const result = addToStore
        ? await StoreInventoryApi.add(storeId, {
            gtin,
            priceTagBarCode: trimmed,
            hasPriceTag: true
          })
        : await StoreInventoryApi.update(storeId, {
            productId,
            gtin,
            priceTagBarCode: trimmed,
            hasPriceTag: true
          });

      if (isOk(result)) {
        setState(s => ({ ...s, loading: false, priceTagBarCode: "" }));
        onSuccess();
      } else {
        setState(s => ({
          ...s,
          loading: false,
          error: result.details.title
        }));
      }
    },
    [addToStore, storeId, productId, gtin, onSuccess]
  );

  const handleScan = useCallback(
    async (data: string, symbology: Symbology) => {
      if (!data) return;

      if (symbology !== Symbology.Code128) {
        setState(s => ({
          ...s,
          error: t("common.priceTagConnection.invalidBarcodeForDigitalPriceTag")
        }));
        return;
      }

      await addProductAndLinkPriceTag(data);
    },
    [addProductAndLinkPriceTag, t]
  );

  return (
    <>
      <Alert severity="info" style={{ marginTop: 0, marginBottom: 10 }}>
        {t("common.priceTagConnection.scanDigitalPriceTagToConnect")}
      </Alert>

      <div
        className={classes.scanner}
        style={
          mobile
            ? { display: "flex", height: "70%", position: "relative" }
            : { marginBottom: 30, position: "relative" }
        }
      >
        <div
          style={{
            height: "100%",
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
            background: "#000"
          }}
        >
          {/* Scanner layer */}
          <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
            <ScanditBarcodeScanner
              licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
              libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
              style={{ width: "100%", height: "100%" }}
              onScan={handleScan}
              paused={state.loading}
            />
          </div>

          {/* Bottom bar overlay (always visible) */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              padding: "12px 16px",
              paddingBottom: "max(12px, env(safe-area-inset-bottom))",
              backgroundColor: "#f5f5f5",
              borderTop: "1px solid #ddd"
            }}
          >
            <TextField
              fullWidth
              disabled={state.loading}
              className={classes.manualInput}
              type="text"
              label={t("common.formFields.manualInput")}
              value={state.priceTagBarCode ?? ""}
              // prevent iOS Safari focus-zoom
              inputProps={{ style: { fontSize: 16 } }}
              InputLabelProps={{
                className: classes.manualInputLabel,
                style: { fontSize: 16 }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      disabled={state.loading}
                      onClick={async () => {
                        await addProductAndLinkPriceTag(state.priceTagBarCode);
                      }}
                    >
                      {t("common.ok")}
                    </Button>
                    <Button
                      disabled={state.loading}
                      onClick={() => {
                        setState(s => ({
                          ...s,
                          loading: false,
                          priceTagBarCode: "",
                          error: ""
                        }));
                        onCancel();
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                  </InputAdornment>
                )
              }}
              onChange={e =>
                setState(s => ({
                  ...s,
                  priceTagBarCode: e.target.value,
                  error: ""
                }))
              }
            />
          </div>

          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 92
            }}
          />
        </div>
      </div>

      {state.loading && <LinearProgress />}

      {state.error && (
        <div
          style={{ display: "flex", marginTop: 20, justifyContent: "center" }}
        >
          <Alert severity="error">{state.error}</Alert>
        </div>
      )}
    </>
  );
};
